import { Award, Scale, Shield, TrendingDown, Users, Clock, Building2 } from "lucide-react";
import { useCounterAnimation } from "@/hooks/use-counter-animation";
import legitScriptBadge from "@/assets/legitscript-certified.png";

const stats = [
  { value: 57, suffix: "k+", label: "Weight Loss Results", icon: Award },
  { value: 1, suffix: "m+", label: "Pounds Lost", icon: Scale },
  { value: 15, suffix: " lbs", label: "Avg loss on 3-mo plan", icon: TrendingDown },
];

const AnimatedStat = ({ stat, delay }: { stat: typeof stats[0]; delay: number }) => {
  const { count, elementRef } = useCounterAnimation({ 
    end: stat.value, 
    duration: 2000,
    delay 
  });
  
  return (
    <div 
      ref={elementRef}
      className="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group border border-gray-100"
    >
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0D1B2A] to-[#1B3A4B] flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
        <stat.icon className="w-7 h-7 text-[#ffdb4d]" />
      </div>
      <div className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0D1B2A] mb-2 tracking-tight">
        {count}{stat.suffix}
      </div>
      <p className="text-sm md:text-base text-gray-500 font-medium uppercase tracking-wider">
        {stat.label}
      </p>
    </div>
  );
};

const EmployerStats = () => {
  return (
    <section className="py-20 bg-gray-50 relative">
      {/* Subtle pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, #0D1B2A 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }} />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <div className="text-center mb-12">
          <h2 className="text-sm font-bold text-[#0D1B2A]/60 uppercase tracking-[0.2em] mb-3">Proven Results</h2>
          <p className="text-2xl md:text-3xl font-semibold text-[#0D1B2A]">The numbers speak for themselves</p>
        </div>
        
        {/* Animated Stats Grid - 3 columns for 3 stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12 max-w-4xl mx-auto">
          {stats.map((stat, index) => (
            <AnimatedStat key={stat.label} stat={stat} delay={index * 150} />
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="flex flex-col items-center gap-6">
          {/* LegitScript Badge */}
          <div className="flex items-center justify-center gap-4 bg-white px-8 py-4 rounded-2xl shadow-md border border-gray-100">
            <img 
              src={legitScriptBadge} 
              alt="LegitScript Certified" 
              className="h-14 md:h-16"
            />
            <div className="text-left">
              <span className="block text-sm font-semibold text-[#0D1B2A]">LegitScript Certified</span>
              <span className="text-xs text-gray-500">Verified Healthcare Provider</span>
            </div>
          </div>

          {/* Trust Badges Row */}
          <div className="flex flex-wrap items-center justify-center gap-4">
            <div className="flex items-center gap-2 px-5 py-3 bg-[#0D1B2A] text-white rounded-full shadow-lg">
              <Shield className="w-5 h-5 text-[#ffdb4d]" />
              <span className="text-sm font-semibold">HIPAA Compliant</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 bg-[#0D1B2A] text-white rounded-full shadow-lg">
              <Clock className="w-5 h-5 text-[#ffdb4d]" />
              <span className="text-sm font-semibold">Telehealth Enabled</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmployerStats;
